import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { authRateLimiter } from '../utils/securityUtils';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, firstName, lastName) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    // Store additional user info in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      firstName,
      lastName,
      email
    });
    // Manually set current user to include firestore data
    setCurrentUser({
      uid: user.uid,
      email: user.email,
      firstName,
      lastName
    });
    return userCredential;
  }

  async function login(email, password) {
    try {
      // Apply rate limiting
      const ip = 'client-ip'; // In a real app, this would come from the server
      const rateCheck = authRateLimiter.checkAttempt(email, ip);
      
      if (!rateCheck.allowed) {
        const errorMsg = rateCheck.lockoutTime > 0 ?
          `Too many login attempts. Please try again in ${Math.ceil(rateCheck.lockoutTime / 60)} minutes.` :
          'Too many login attempts. Please try again later.';
        throw new Error(errorMsg);
      }
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Reset attempts counter on successful login
      authRateLimiter.resetAttempts(email, ip);
      
      return userCredential;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  function logout() {
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Fetch additional user data from Firestore
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setCurrentUser({
            uid: user.uid,
            email: user.email,
            ...userDoc.data()
          });
        } else {
          // Fallback if no firestore doc exists
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
